/* Orchid Loyalty Rules — configuration UI for the 21 masters that drive the
   loyalty engine (tier table, store groups, item exclusions, BS/MS masters).

   Every list is edited as "one value per line" for speed. Tier table is a
   structured row editor. Save writes back to `/api/loyalty/masters/{type}`
   and flips `locked_by_admin=true` so the boot-time seed won't overwrite.
   The `Unlock & re-seed` button resets a master to the doc defaults. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "./_shared";
import { toast } from "sonner";
import { Save, RefreshCcw, Plus, Trash2, ShieldCheck, Beaker, Lock, Unlock } from "lucide-react";

const GROUPS = [
  {
    key: "tiers", label: "Tier Table", icon: ShieldCheck,
    types: [{ type: "tier_table", label: "Tier / Slab master (rate per ₹)", structured: true }],
  },
  {
    key: "stores", label: "Store Groups",
    types: [
      { type: "hotel_stores",             label: "Hotel Live Stores (Hotel earn logic)" },
      { type: "restaurant_stores",        label: "Restaurant Live Stores (Restaurant earn logic)" },
      { type: "banquet_stores",           label: "Banquet Live Stores" },
      { type: "bs_ms_required_stores",    label: "Stores that require BS + MS match" },
      { type: "banquet_slab_stores",      label: "Banquet-slab stores (use Banquet tier %)" },
      { type: "slab_excluded_stores",     label: "Slab-excluded stores (amount ≠ tier progress)" },
      { type: "lonavala_stores",          label: "Lonavala stores — earning stopped" },
      { type: "banquet_licensing_stores", label: "Banquet_05_110611-style stores (item-whitelist)" },
    ],
  },
  {
    key: "items", label: "Item Exclusions",
    types: [
      { type: "skip_item_codes",         label: "Skip item codes (dropped entirely — e.g. 00000030)" },
      { type: "excluded_item_codes",     label: "Excluded item codes (no points)" },
      { type: "excluded_item_names",     label: "Excluded item names (LCD, SCREEN, LIQUOR, etc.)" },
      { type: "banquet_licensing_items", label: "Whitelist items for licensing stores (00000010/11/12)" },
    ],
  },
  {
    key: "blockers", label: "Bill Blockers",
    types: [
      { type: "blocked_bill_types", label: "Blocked bill types (BTC, ENTBILLLIQ, PREPAIDFOR, …)" },
      { type: "blocked_tenders",    label: "Blocked tender types (BILL TO COMPANY)" },
      { type: "blocked_companies",  label: "Blocked company names (BANK OF BARODA)" },
    ],
  },
  {
    key: "sources", label: "BS + MS Masters",
    types: [
      { type: "business_sources", label: "Business Sources — approved values" },
      { type: "market_segments",  label: "Market Segments — approved values" },
    ],
  },
  {
    key: "billsrc", label: "Bill Source Map",
    types: [
      { type: "hotel_bill_sources",      label: "Bill sources → treated as HOTEL" },
      { type: "restaurant_bill_sources", label: "Bill sources → treated as RESTAURANT" },
      { type: "banquet_bill_sources",    label: "Bill sources → treated as BANQUET" },
    ],
  },
];

export default function OrchidLoyaltyRules() {
  const [active, setActive] = useState("tiers");
  const [counts, setCounts] = useState({});
  const [locks, setLocks] = useState({});
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    api.get("/loyalty/masters").then((r) => {
      const c = {}, l = {};
      (r.data.masters || []).forEach((m) => { c[m.master_type] = m.count; l[m.master_type] = m.locked_by_admin; });
      setCounts(c); setLocks(l);
    }).catch(() => {});
  }, [reloadKey]);

  const grp = GROUPS.find((g) => g.key === active);

  return (
    <div className="p-6 space-y-4" data-testid="orchid-loyalty-rules">
      <PageHeader
        title="Orchid Loyalty Rules"
        subtitle="Every earn / block / gating rule the Fundle-Loyalty-API engine applies to new INNKeys bills. Edits go live on the next bill."
      />
      <div className="flex flex-wrap gap-1 border-b border-black/10">
        {GROUPS.map((g) => (
          <button
            key={g.key}
            onClick={() => setActive(g.key)}
            data-testid={`tab-${g.key}`}
            className={`px-3 py-2 text-sm border-b-2 -mb-px ${active === g.key ? "border-amber-600 text-amber-900 font-medium" : "border-transparent text-neutral-600 hover:text-neutral-900"}`}
          >{g.label}</button>
        ))}
        <button
          onClick={() => setActive("simulator")}
          data-testid="tab-simulator"
          className={`ml-auto px-3 py-2 text-sm border-b-2 -mb-px flex items-center gap-1 ${active === "simulator" ? "border-amber-600 text-amber-900 font-medium" : "border-transparent text-neutral-600 hover:text-neutral-900"}`}
        ><Beaker className="w-3.5 h-3.5" /> Rule Simulator</button>
      </div>

      {active === "simulator" ? (
        <SimulatorTab />
      ) : (
        <div className="space-y-6">
          {grp.types.map((t) => (
            <MasterEditor
              key={t.type} type={t.type} label={t.label}
              structured={!!t.structured}
              count={counts[t.type] ?? 0}
              locked={!!locks[t.type]}
              onSaved={() => setReloadKey((k) => k + 1)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MasterEditor({ type, label, structured, count, locked, onSaved }) {
  const [values, setValues] = useState(null);
  const [txt, setTxt] = useState("");
  const [tiers, setTiers] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/loyalty/masters/${type}`);
      const v = r.data.values || [];
      setValues(v);
      if (structured) setTiers(v);
      else setTxt(v.map((x) => (typeof x === "string" ? x : JSON.stringify(x))).join("\n"));
      setDirty(false);
    } catch (e) {
      toast.error(`Failed to load ${type}`);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [type]);

  const save = async () => {
    let out;
    if (structured) out = tiers;
    else out = txt.split("\n").map((s) => s.trim()).filter(Boolean);
    setSaving(true);
    try {
      await api.put(`/loyalty/masters/${type}`, { values: out, locked_by_admin: true });
      toast.success(`${label} saved — ${out.length} entries · engine cache refreshed`);
      setDirty(false); onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const unlock = async () => {
    if (!confirm(`Reset "${label}" to the Loyalty Doc defaults on the next backend boot?`)) return;
    try {
      await api.post(`/loyalty/masters/${type}/unlock`);
      toast.success("Unlocked — restart backend to re-seed from the doc");
      onSaved?.();
    } catch (e) { toast.error("Unlock failed"); }
  };

  return (
    <div className="border border-black/10 bg-white" data-testid={`master-${type}`}>
      <div className="flex items-center justify-between px-4 py-2 border-b border-black/10 bg-neutral-50">
        <div>
          <div className="text-sm font-medium text-neutral-900">{label}</div>
          <div className="text-[11px] text-neutral-500 flex items-center gap-2">
            <code className="font-mono">{type}</code>
            <span>· {count} entries</span>
            {locked ? (
              <span className="inline-flex items-center gap-0.5 text-amber-700"><Lock className="w-3 h-3" /> admin-locked</span>
            ) : (
              <span className="inline-flex items-center gap-0.5 text-neutral-400"><Unlock className="w-3 h-3" /> auto-seeded</span>
            )}
            {dirty && <span className="text-amber-600 font-medium">· unsaved</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={unlock} className="text-xs px-2 py-1 border border-black/10 text-neutral-600 hover:bg-neutral-100"
                  data-testid={`unlock-${type}`}>
            <RefreshCcw className="w-3 h-3 inline mr-1" /> Reset to doc
          </button>
          <button onClick={save} disabled={saving || !dirty}
                  className="text-xs px-3 py-1 bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-40"
                  data-testid={`save-${type}`}>
            <Save className="w-3 h-3 inline mr-1" /> {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
      <div className="p-4">
        {loading ? (
          <div className="text-sm text-neutral-500">Loading…</div>
        ) : structured ? (
          <TierTable rows={tiers} onChange={(v) => { setTiers(v); setDirty(true); }} />
        ) : (
          <>
            <textarea
              value={txt}
              onChange={(e) => { setTxt(e.target.value); setDirty(true); }}
              rows={Math.min(20, Math.max(6, txt.split("\n").length + 1))}
              placeholder="One value per line — case-sensitive"
              className="w-full font-mono text-xs p-2 border border-black/10 focus:border-amber-500 focus:outline-none"
              data-testid={`ta-${type}`}
            />
            <div className="text-[11px] text-neutral-500 mt-1">
              {txt.split("\n").filter((s) => s.trim()).length} lines
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TierTable({ rows, onChange }) {
  const upd = (i, k, v) => {
    const a = [...rows]; a[i] = { ...a[i], [k]: v }; onChange(a);
  };
  const add = () => onChange([...rows, {
    s_no: rows.length + 1, tier_name: "New Tier", min_amount: 0,
    max_amount: 100000000, tier_percent: 1.0, tier_type: "regular"
  }]);
  const rm = (i) => { const a = [...rows]; a.splice(i, 1); onChange(a); };
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-[11px] uppercase tracking-wider text-neutral-500 border-b border-black/10">
          <tr>
            <th className="py-2 text-left w-12">S.No</th>
            <th className="py-2 text-left">Tier Name</th>
            <th className="py-2 text-right">Min ₹</th>
            <th className="py-2 text-right">Max ₹</th>
            <th className="py-2 text-right w-24">Rate %</th>
            <th className="py-2 text-left w-24">Type</th>
            <th className="py-2 w-10"></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-black/5" data-testid={`tier-row-${i}`}>
              <td className="py-1"><input type="number" className="k-input k-input-sm !w-14" value={r.s_no ?? i + 1} onChange={(e) => upd(i, "s_no", Number(e.target.value))} /></td>
              <td className="py-1"><input className="k-input k-input-sm w-full" value={r.tier_name ?? ""} onChange={(e) => upd(i, "tier_name", e.target.value)} /></td>
              <td className="py-1"><input type="number" className="k-input k-input-sm !w-36 text-right" value={r.min_amount ?? 0} onChange={(e) => upd(i, "min_amount", Number(e.target.value))} /></td>
              <td className="py-1"><input type="number" className="k-input k-input-sm !w-36 text-right" value={r.max_amount ?? 0} onChange={(e) => upd(i, "max_amount", Number(e.target.value))} /></td>
              <td className="py-1"><input type="number" step="0.1" className="k-input k-input-sm !w-20 text-right" value={r.tier_percent ?? 0} onChange={(e) => upd(i, "tier_percent", Number(e.target.value))} /></td>
              <td className="py-1"><input className="k-input k-input-sm !w-24" value={r.tier_type ?? "regular"} onChange={(e) => upd(i, "tier_type", e.target.value)} /></td>
              <td className="py-1 text-right"><button onClick={() => rm(i)} className="text-neutral-400 hover:text-red-600" data-testid={`tier-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={add} className="mt-2 text-xs text-amber-800 hover:text-amber-900 inline-flex items-center gap-1"
              data-testid="tier-add"><Plus className="w-3.5 h-3.5" /> Add tier</button>
    </div>
  );
}

const SAMPLE_BILL = {
  store_code: "Front_110602",
  bill_no: "SIM-001",
  bill_date: "2026-02-25",
  bill_time: "2026-02-25 18:00:00",
  bill_type: "Sale",
  bill_transaction_type: "Sale",
  bill_tender_type: "Card",
  bill_business_source: "FDS - FRONT DESK",
  bill_market_segment: "FIT - FREE INDIVIDUAL TRAVELLER",
  bill_gross_amount: "10000",
  bill_net_amount: "11000",
  bill_tax: "1000",
  bill_discount: "0",
  bill_discount_per: "0",
  bill_grand_total: "11000",
  bill_transaction_no: "SIM-TX-1",
  customer_mobile: "9990000001",
  output: [{
    item_serial_no: "1", item_code: "ROOM-1N", item_name: "Deluxe Room",
    item_rate: "10000", item_gross_amount: "10000", item_net_amount: "11000",
    item_quantity: "1", item_tax: "1000",
  }],
};

function SimulatorTab() {
  const [json, setJson] = useState(JSON.stringify(SAMPLE_BILL, null, 2));
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true); setResult(null);
    let parsed;
    try { parsed = JSON.parse(json); }
    catch (e) { toast.error("JSON parse error: " + e.message); setBusy(false); return; }
    try {
      const r = await api.post("/loyalty/masters/simulate", { bill: parsed });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulate failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="simulator-panel">
      <div className="border border-black/10 bg-white">
        <div className="px-3 py-2 border-b border-black/10 bg-neutral-50 text-sm font-medium">Sample bill (edit and re-run)</div>
        <textarea rows={26} className="w-full font-mono text-xs p-3 border-0 focus:outline-none"
                  value={json} onChange={(e) => setJson(e.target.value)}
                  data-testid="sim-input" />
        <div className="p-3 border-t border-black/10 flex justify-between items-center">
          <button onClick={() => setJson(JSON.stringify(SAMPLE_BILL, null, 2))}
                  className="text-xs text-neutral-500 hover:text-neutral-800" data-testid="sim-reset">Reset sample</button>
          <button onClick={run} disabled={busy}
                  className="px-3 py-1.5 bg-amber-600 text-white text-xs hover:bg-amber-700 disabled:opacity-40"
                  data-testid="sim-run">{busy ? "Running…" : "Run rule engine"}</button>
        </div>
      </div>
      <div className="border border-black/10 bg-white">
        <div className="px-3 py-2 border-b border-black/10 bg-neutral-50 text-sm font-medium">Engine verdict</div>
        <div className="p-4">
          {result ? (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <span className={`inline-flex px-2 py-0.5 text-xs font-medium ${result.points > 0 ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                  {result.points > 0 ? `+${result.points} pts` : `${result.points} pts (blocked)`}
                </span>
                <span className="text-neutral-600">at <b>{result.tier_used || "—"}</b></span>
                <span className="text-neutral-400">· {result.category}</span>
              </div>
              <div className="text-xs text-neutral-600">{result.reason}</div>
              <div className="grid grid-cols-2 gap-2 text-xs pt-2 border-t border-black/5">
                <div><span className="text-neutral-500">Eligible base:</span> <b>₹{Number(result.eligible_base || 0).toLocaleString("en-IN")}</b></div>
                <div><span className="text-neutral-500">Counts toward tier:</span> <b>{result.slab_countable ? "Yes" : "No"}</b></div>
              </div>
              <details className="pt-2 border-t border-black/5">
                <summary className="text-xs text-neutral-500 cursor-pointer">Full response</summary>
                <pre className="font-mono text-[10px] bg-neutral-50 p-2 mt-1 overflow-x-auto">{JSON.stringify(result, null, 2)}</pre>
              </details>
            </div>
          ) : (
            <div className="text-neutral-400 text-sm">Fill the bill JSON on the left and hit <b>Run rule engine</b>.</div>
          )}
        </div>
      </div>
    </div>
  );
}
